import { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import Button from '@components/atoms/ui-kit/button.tsx';
import UploadButton from '@components/shared/upload/upload-button.tsx';
import { useGetFolder } from '@utils/api/folders/use-get-folder.tsx';
import { CreateFolder } from '@components/workspace/create-folder.tsx';
import { useTranslation } from 'react-i18next';
import PageHeader from '@components/layout/page-header.tsx';
import { Workspace } from '@components/main-workspace/workspace.tsx';
import { useNewAppSearchParams } from '@utils/hooks/use-new-app-search-params.ts';
import { Folder } from 'lucide-react';
import {
  BreadcrumbItem,
  Breadcrumbs
} from '@components/atoms/ui-kit/breadcrumbs.tsx';
import { buildFolderBreadcrumbTrail } from '@utils/breadcrump-trails/build-folder-breadcrumb-trail.ts';
import { SortOrderToggle } from '@components/workspace/sort-order-toggle.tsx';
import {
  getSortOrder,
  setSortOrder,
  type SortOrder
} from '@utils/localStorage/sort-order-storage.ts';

type Params = {
  folderId?: string;
  sessionId?: string;
  documentId?: string;
};

export default function FolderContents() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { folderId } = useParams<Params>();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { searchParams } = useNewAppSearchParams();
  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbItem[]>([]);
  const [sortOrder, setSortOrderState] = useState<SortOrder>(getSortOrder);

  const handleSortOrderChange = (order: SortOrder) => {
    setSortOrderState(order);
    setSortOrder(order);
  };

  // Read all q parameters from URL for multi-filter support
  const allQ = useMemo(() => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.getAll('q');
  }, [searchParams.q]); // Re-compute when q param changes

  // Convert sortOrder to backend sortType
  const sortType =
    sortOrder === 'folders-first'
      ? 'folders-first'
      : sortOrder === 'files-first'
        ? 'files-first'
        : undefined;

  const { data, isLoading, isFetching, error } = useGetFolder(folderId, {
    offset: parseInt(searchParams?.offset || '0'),
    limit: parseInt(searchParams?.limit || '25'),
    q: allQ.length > 0 ? allQ : undefined,
    sort: searchParams?.sort as string | undefined,
    direction: (searchParams?.direction as 'asc' | 'desc') || undefined,
    sortType,
    from: searchParams?.from as string | undefined,
    to: searchParams?.to as string | undefined
  });

  // Handle 404 or 422 - folder doesn't exist (might have been deleted)
  useEffect(() => {
    if (error?.response?.status === 404 || error?.response?.status === 422) {
      toast.error(t('folder-not-found') || 'Folder not found');
      navigate('/');
    }
  }, [error, navigate, t]);

  const fetchData = {
    data: data,
    isLoading: isLoading,
    isFetching: isFetching
  };

  useEffect(() => {
    if (data?.result) {
      buildFolderBreadcrumbTrail(data.result).then(setBreadcrumbs);
    }
  }, [data?.result]);

  return (
    <div className="bg-app flex-1 min-h-0 flex flex-col gap-y-4 pb-2">
      <CreateFolder
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
      <Workspace
        fetchData={fetchData}
        title="Workspace Title"
        caption="This is a caption for the workspace."
        folderId={folderId}
      >
        <PageHeader
          title={
            isLoading || !data?.result ? (
              t('...')
            ) : (
              <Breadcrumbs
                items={breadcrumbs}
                currentFolderName={data.result.name}
              />
            )
          }
          caption={t(
            'a-list-of-all-the-files-in-your-account-including-their-name-duration-language-and-status'
          )}
        >
          <SortOrderToggle
            sortOrder={sortOrder}
            onSortOrderChange={handleSortOrderChange}
          />

          <UploadButton />

          <Button
            label={t('create-folder')}
            onClick={() => setIsModalOpen(true)}
            size="medium"
            className="ml-2"
            variant="outline"
            icon={<Folder size={16} className="mr-1" />}
          />
        </PageHeader>
      </Workspace>
    </div>
  );
}
